import { createFileRoute, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { HeroBanner } from "@/components/HeroBanner";
import { Calendar, ArrowRight } from "lucide-react";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { buildSeoMeta } from "@/lib/seo";

const BREADCRUMB_JSON_LD = buildBreadcrumbJsonLd([
  { name: "Forside", path: "" },
  { name: "Blog", path: "blog" },
]);

export const Route = createFileRoute("/blog")({
  head: () => buildSeoMeta({
    title: "Blog · Bilguides og nyheder fra AutoXChange",
    description:
      "Guides, nyheder og ærlige tanker om bilkøb, vedligeholdelse og det danske bilmarked — fra teamet bag AutoXChange i Børkop.",
    path: "/blog",
  }),
  component: Blog,
});

function Blog() {
  const { data: posts, isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("status", "published")
        .order("published_at", { ascending: false });
      return data || [];
    },
  });

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: BREADCRUMB_JSON_LD }} />
      <HeroBanner title="Fra garagen" subtitle="Guides, erfaringer og ærlige tanker om biler og bilkøb" />

      <section className="bg-background py-16">
        <div className="mx-auto max-w-7xl px-6">
          {isLoading ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-80 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : posts && posts.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to="/blog/$slug"
                  params={{ slug: post.slug }}
                  className="group overflow-hidden rounded-lg border border-border bg-card transition-all hover:shadow-lg"
                >
                  {post.featured_image && (
                    <div className="aspect-[16/9] overflow-hidden bg-muted">
                      <img src={post.featured_image} alt={post.title} width={800} height={450} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" decoding="async" />
                    </div>
                  )}
                  <div className="p-6">
                    {post.tags && post.tags.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {post.tags.map((tag) => (
                          <span key={tag} className="rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-medium text-accent">{tag}</span>
                        ))}
                      </div>
                    )}
                    <h2 className="text-lg font-bold text-card-foreground group-hover:text-accent transition-colors">{post.title}</h2>
                    {post.excerpt && <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>}
                    <div className="mt-4 flex items-center justify-between">
                      {post.published_at && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar size={12} /> {new Date(post.published_at).toLocaleDateString("da-DK")}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs font-medium text-accent">
                        Læs mere <ArrowRight size={12} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center">
              <p className="text-lg font-medium text-muted-foreground">Vi har ikke skrevet noget endnu</p>
              <p className="mt-2 text-sm text-muted-foreground">Men det kommer — hold øje med denne side.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
